// Helper to check for truly empty values (undefined, null, '', [], {}) but not numbers or booleans
export const isTrulyEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    Object.keys(value).length === 0);

const isNotEmpty = (item: unknown): boolean => {
  if (item === null || item === undefined) {
    return false;
  }
  if (typeof item === 'string') {
    return item.trim() !== '';
  }
  return true;
};

interface IsEmptyValueOptions {
  excludeKeys?: Array<string> | ((value: unknown) => Array<string>);
  recursive?: boolean;
}

// Enhanced version that also considers arrays with only empty values (null, undefined, '') as empty
// This is useful for filtering search parameters where arrays like ["", null] should be treated as empty
export const isEmptyValue = (
  value: unknown,
  options: IsEmptyValueOptions = {},
): boolean => {
  const { excludeKeys = [], recursive = false } = options;

  if (Array.isArray(value)) {
    const filteredArray = value.filter(isNotEmpty);
    return filteredArray.length === 0;
  }

  if (typeof value === 'object' && value !== null) {
    if (value instanceof Date) {
      return false;
    }

    const entries = Object.entries(value);
    const keysToExclude =
      typeof excludeKeys === 'function' ? excludeKeys(value) : excludeKeys;
    const entriesToCheck =
      keysToExclude.length > 0
        ? entries.filter(([key]) => !keysToExclude.includes(key))
        : entries;

    if (entriesToCheck.length === 0) {
      return true;
    }

    if (recursive) {
      return entriesToCheck.every(([, nestedValue]) =>
        isEmptyValue(nestedValue, options),
      );
    }

    return false;
  }

  return !isNotEmpty(value);
};

// Cleans the value by filtering out empty strings, null, and undefined from arrays
export const filterEmptyValues = <T>(
  value: T,
): T extends Array<infer U> ? Array<U> | null : T => {
  if (Array.isArray(value)) {
    const filtered = value.filter(isNotEmpty);
    return (filtered.length > 0 ? filtered : null) as any;
  }

  return value as any;
};
